import { FC, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';

import styles from './TripHeader.module.scss';
import { Trip } from '../../../../../types/trip.types';
import CustomButton from '../../../../../common/CustomButton';
import { formatDateRangeWithDuration } from '../../../../../utils/dateUtils';
import {
  MergeIcon,
  CalendarActiveIcon,
  LocationActiveIcon,
  AiIcon,
  ExpandIcon,
  RecentlyEditedIcon,
  DeleteIcon,
  EditIcon,
} from '../../../../../components/Icons';
import { ChevronLeft } from '@mui/icons-material';
import ThreeDotsMenu, { MenuItemConfig } from '../../../../../components/ThreeDotsMenu/ThreeDotsMenu';
import MergeTripsModal from '../../../MyTrips/components/MergeTripsModal/MergeTripsModal';
import TripCoverUploadModal from '../../../MyTrips/components/TripCoverUploadModal/TripCoverUploadModal';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import { useDeleteTripCustomCoverMutation } from '../../../../../store/services/tripsApi';
import EventReminderSettings, {
  getTripReminderEndIso,
  getTripReminderStartIso,
} from '../../../../../common/EventReminderSettings/EventReminderSettings';
import ConfirmActionModal from '../../../../../common/ConfirmActionModal/ConfirmActionModal';

type PendingTripConfirm = null | 'trip' | 'cover';

interface TripHeaderProps {
  trip: Trip;
  onBackClick: () => void;
  onAIAssistant: () => void;
  onMergeSuccess?: () => void;
  onEditTrip?: () => void;
  onDeleteTrip?: () => void | Promise<void>;
  rightPanelVisible: boolean;
  onExpandClick: () => void;
}

const TripHeader: FC<TripHeaderProps> = ({
  trip,
  onBackClick,
  onAIAssistant,
  onMergeSuccess,
  onEditTrip,
  onDeleteTrip,
  rightPanelVisible,
  onExpandClick,
}) => {
  const isMobile = useIsMobile();
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showTripCoverModal, setShowTripCoverModal] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<PendingTripConfirm>(null);
  const [isDeletingTrip, setIsDeletingTrip] = useState(false);
  const [deleteTripCustomCover, { isLoading: isDeletingCover }] = useDeleteTripCustomCoverMutation();
  const tripCoverUrl = trip.custom_cover;

  const tripMenuItems: MenuItemConfig[] = [
    {
      id: 'trip-cover-upload',
      label: tripCoverUrl ? 'Change cover' : 'Upload cover',
      icon: <EditIcon />,
      disabled: isDeletingCover,
      onClick: () => {
        setShowTripCoverModal(true);
      },
    },
    ...(tripCoverUrl
      ? [
          {
            id: 'trip-cover-delete',
            label: 'Delete cover',
            icon: <DeleteIcon />,
            variant: 'danger' as const,
            disabled: isDeletingCover,
            onClick: () => {
              setPendingConfirm('cover');
            },
          },
        ]
      : []),
    {
      id: 'merge',
      label: 'Merge Trip',
      icon: (
        <MergeIcon
          className={styles.buttonIcon}
          size={24}
        />
      ),
      onClick: () => {
        handleMergeClick?.();
      },
    },
    {
      id: 'edit',
      label: 'Edit Trip',
      icon: <RecentlyEditedIcon />,
      onClick: () => {
        onEditTrip?.();
      },
    },
    ...(trip.is_user_owner
      ? [
          {
            id: 'delete',
            label: 'Delete Trip',
            icon: <DeleteIcon />,
            variant: 'danger' as const,
            onClick: () => {
              setPendingConfirm('trip');
            },
          },
        ]
      : []),
  ];

  const handleMergeClick = () => {
    setShowMergeModal(true);
  };

  const handleMergeModalClose = () => {
    setShowMergeModal(false);
  };

  const handleMergeSuccess = () => {
    onMergeSuccess?.();
    setShowMergeModal(false);
  };

  const handleTripCoverModalClose = () => {
    setShowTripCoverModal(false);
  };

  const performCoverDelete = async () => {
    try {
      await deleteTripCustomCover(trip.id).unwrap();
      setPendingConfirm(null);
    } catch (error) {
      console.error('Failed to delete trip cover:', error);
    }
  };

  const performTripDelete = async () => {
    if (!onDeleteTrip) return;
    setIsDeletingTrip(true);
    try {
      await Promise.resolve(onDeleteTrip());
      setPendingConfirm(null);
    } finally {
      setIsDeletingTrip(false);
    }
  };

  return (
    <>
      <Box className={styles.tripHeader}>
        <Box
          className={styles.headerBackground}
          sx={{
            backgroundImage: `url(${tripCoverUrl || require('../../../../../assets/images/TripMockDetails.jpg')})`,
          }}
        />
        <Box className={styles.headerContent}>
          <Box className={styles.headerActions}>
            <Box className={styles.actionButtons}>
              <IconButton
                onClick={onBackClick}
                className={styles.backButton}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              {!isMobile && (
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  gap={'12px'}
                >
                  <Box>
                    <ThreeDotsMenu
                      menuItems={tripMenuItems}
                      iconSize={32}
                      className={styles.headerMenuButton}
                    />
                  </Box>
                  <Box>
                    {!rightPanelVisible && (
                      <IconButton
                        onClick={onExpandClick}
                        className={styles.expandButton}
                        size="small"
                      >
                        <ExpandIcon size={32} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}
              {isMobile && (
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  gap={'12px'}
                >
                  <Box>
                    <ThreeDotsMenu
                      menuItems={tripMenuItems}
                      iconSize={32}
                      className={styles.headerMenuButton}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          <Box className={styles.tripInfoContainer}>
            <Box className={styles.tripInfoBlock}>
              <Box className={styles.tripInfo}>
                <Typography
                  variant={isMobile ? 'h5Bold' : 'h4Bold'}
                  className={styles.tripTitle}
                >
                  {trip.name}
                </Typography>

                <Box className={styles.tripDetailRow}>
                  <CalendarActiveIcon className={styles.detailIcon} />
                  <Typography
                    variant="h11"
                    className={styles.tripDates}
                  >
                    {formatDateRangeWithDuration(trip.start_date, trip.end_date)}
                  </Typography>
                </Box>

                <Box className={styles.tripDetailRow}>
                  <LocationActiveIcon className={styles.detailIcon} />
                  <Typography
                    variant="h11"
                    className={styles.tripRoute}
                  >
                    {trip.destination_countries.map(country => country.name).join(', ')}
                  </Typography>
                </Box>
              </Box>
              {!isMobile && (
                <CustomButton
                  variant="contained"
                  onClick={onAIAssistant}
                  customClass={styles.aiButton}
                  startIcon={<AiIcon />}
                >
                  AI Assistant
                </CustomButton>
              )}
            </Box>
            <Box className={styles.tripReminderRow}>
              <EventReminderSettings
                scope="trip"
                tripId={trip.id}
                compact
                eventStartsAtIso={getTripReminderStartIso(trip.start_date)}
                eventEndsAtIso={getTripReminderEndIso(trip.end_date)}
              />
            </Box>
          </Box>
          {isMobile && (
            <Box className={styles.mobileAiButtonContainer}>
              <CustomButton
                variant="contained"
                onClick={onAIAssistant}
                customClass={styles.mobileAiButton}
                startIcon={<AiIcon />}
              >
                AI Assistant
              </CustomButton>
            </Box>
          )}
        </Box>
      </Box>
      <MergeTripsModal
        open={showMergeModal}
        onClose={handleMergeModalClose}
        selectedTrip={trip}
        onSuccess={handleMergeSuccess}
      />
      <TripCoverUploadModal
        open={showTripCoverModal}
        onClose={handleTripCoverModalClose}
        tripId={trip.id}
      />
      <ConfirmActionModal
        open={pendingConfirm !== null}
        onClose={() => setPendingConfirm(null)}
        onConfirm={pendingConfirm === 'trip' ? performTripDelete : performCoverDelete}
        description={
          pendingConfirm === 'trip'
            ? 'Do you really want to delete this trip?'
            : 'Do you really want to delete the cover image?'
        }
        confirmLabel={pendingConfirm === 'trip' ? 'Delete Trip' : 'Delete'}
        isConfirming={pendingConfirm === 'trip' ? isDeletingTrip : isDeletingCover}
      />
    </>
  );
};

export default TripHeader;
